import { drawGlow, glowText, withAlpha, type ArtKit, type Ctx2D } from "./render";

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  size: number;
  color: string;
  gravity: number;
  spark: boolean;
}

interface FloatText {
  x: number;
  y: number;
  life: number;
  label: string;
  color: string;
  size: number;
}

interface Shockwave {
  x: number;
  y: number;
  life: number;
  max: number;
  radius: number;
  color: string;
}

const MAX_PARTICLES = 320;

// Particles, sparks, shockwaves, floating score text and screen shake shared by
// every game. Everything is additive light drawn from glow sprites. Shake and
// most of the sparkle are dropped under reduced motion.
export class Fx {
  private particles: Particle[] = [];
  private texts: FloatText[] = [];
  private waves: Shockwave[] = [];
  private shake = 0;
  private bannerText = { label: "", sub: "", life: 0, color: "#fff" };
  flash = 0;

  constructor(private rng: () => number, private reduced: boolean, private art: ArtKit, private quiet = false) {}

  burst(x: number, y: number, color: string, count = 14, speed = 180, gravity = 160) {
    const total = this.reduced ? Math.ceil(count / 3) : count;
    for (let i = 0; i < total && this.particles.length < MAX_PARTICLES; i++) {
      const angle = this.rng() * Math.PI * 2;
      const velocity = speed * (0.3 + this.rng() * 0.7);
      const max = 0.35 + this.rng() * 0.5;
      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * velocity,
        vy: Math.sin(angle) * velocity,
        life: max,
        max,
        size: 2 + this.rng() * 4,
        color,
        gravity,
        spark: this.rng() < 0.5,
      });
    }
  }

  trail(x: number, y: number, color: string, vx = 0, vy = 0) {
    if (this.reduced || this.particles.length >= MAX_PARTICLES) return;
    const max = 0.25 + this.rng() * 0.25;
    this.particles.push({ x, y, vx: vx + (this.rng() - 0.5) * 30, vy: vy + (this.rng() - 0.5) * 30, life: max, max, size: 2 + this.rng() * 3, color, gravity: 0, spark: false });
  }

  ring(x: number, y: number, color: string, radius = 80, life = 0.45) {
    if (this.waves.length > 16) this.waves.shift();
    this.waves.push({ x, y, life, max: life, radius, color });
  }

  text(x: number, y: number, label: string, color: string, size = 18) {
    if (this.quiet) return;
    if (this.texts.length > 12) this.texts.shift();
    this.texts.push({ x, y, life: 0.8, label, color, size });
  }

  // A big centred title for level ups, fever and boss warnings.
  banner(label: string, sub: string, color: string) {
    if (this.quiet) return;
    this.bannerText = { label, sub, life: 1.8, color };
    this.flash = Math.min(1, this.flash + 0.4);
  }

  kick(amount: number) {
    this.flash = Math.min(1, this.flash + amount * 0.04);
    if (this.reduced) return;
    this.shake = Math.min(16, this.shake + amount);
  }

  update(dt: number) {
    this.shake = Math.max(0, this.shake - dt * 45);
    this.flash = Math.max(0, this.flash - dt * 3);
    for (const p of this.particles) {
      p.life -= dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vx *= 1 - dt * 2;
      p.vy = p.vy * (1 - dt * 2) + p.gravity * dt;
    }
    this.particles = this.particles.filter((p) => p.life > 0);
    for (const t of this.texts) {
      t.life -= dt;
      t.y -= 42 * dt;
    }
    this.texts = this.texts.filter((t) => t.life > 0);
    this.bannerText.life = Math.max(0, this.bannerText.life - dt);
    for (const w of this.waves) w.life -= dt;
    this.waves = this.waves.filter((w) => w.life > 0);
  }

  applyShake(ctx: Ctx2D) {
    if (this.shake <= 0) return;
    ctx.translate((this.rng() - 0.5) * this.shake, (this.rng() - 0.5) * this.shake);
  }

  draw(ctx: Ctx2D) {
    ctx.save();
    for (const w of this.waves) {
      const t = 1 - w.life / w.max;
      ctx.globalCompositeOperation = "lighter";
      ctx.strokeStyle = withAlpha(w.color, (1 - t) * 0.8);
      ctx.lineWidth = 2 + (1 - t) * 6;
      ctx.beginPath();
      ctx.arc(w.x, w.y, w.radius * (0.2 + t * 0.8), 0, Math.PI * 2);
      ctx.stroke();
    }
    for (const p of this.particles) {
      const fade = Math.max(0, p.life / p.max);
      if (p.spark) {
        ctx.globalCompositeOperation = "lighter";
        ctx.strokeStyle = withAlpha(p.color, fade);
        ctx.lineWidth = Math.max(1, p.size * 0.45);
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
        ctx.lineTo(p.x - p.vx * 0.04, p.y - p.vy * 0.04);
        ctx.stroke();
      } else {
        drawGlow(ctx, this.art, p.color, p.x, p.y, p.size * 3 * (0.5 + fade * 0.5), fade);
      }
    }
    ctx.restore();
    for (const t of this.texts) {
      glowText(ctx, t.label, t.x, t.y, t.size, t.color, Math.min(1, t.life * 2));
    }
  }

  drawBanner(ctx: Ctx2D, width: number, height: number) {
    const banner = this.bannerText;
    if (banner.life <= 0) return;
    const t = 1.8 - banner.life;
    const alpha = Math.min(1, t * 5, banner.life * 2.5);
    const scale = 1 + Math.max(0, 0.25 - t) * 1.2;
    const size = Math.min(54, width * 0.1) * scale;
    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    const band = ctx.createLinearGradient(0, height * 0.4, 0, height * 0.6);
    band.addColorStop(0, withAlpha(banner.color, 0));
    band.addColorStop(0.5, withAlpha(banner.color, 0.22 * alpha));
    band.addColorStop(1, withAlpha(banner.color, 0));
    ctx.fillStyle = band;
    ctx.fillRect(0, height * 0.4, width, height * 0.2);
    ctx.restore();
    glowText(ctx, banner.label, width / 2, height * 0.47, size, banner.color, alpha);
    if (banner.sub) glowText(ctx, banner.sub, width / 2, height * 0.47 + size * 0.85, Math.max(13, size * 0.32), banner.color, alpha * 0.9);
  }
}
